import React,{useState} from 'react';
import {motion,AnimatePresence} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './common/SafeIcon';
import QuestionnaireSection from './components/QuestionnaireSection';
import ThankYouPage from './components/ThankYouPage';
import AdminLogin from './components/AdminLogin';
import AdminDashboard from './components/AdminDashboard';
import {sections as initialSections} from './data/sections';
import './App.css';

const {FiSend,FiShield}=FiIcons;

function App() {
  const [currentStage,setCurrentStage]=useState('questionnaire');
  const [answers,setAnswers]=useState({});
  const [expandedSections,setExpandedSections]=useState(new Set([0]));
  const [adminAuthenticated,setAdminAuthenticated]=useState(false);

  const handleAnswerChange=(questionId,value)=> {
    setAnswers((previous)=> ({...previous,[questionId]: value}));
  };

  const toggleSection=(index)=> {
    setExpandedSections((previous)=> {
      const next=new Set(previous);
      next.has(index) ? next.delete(index) : next.add(index);
      return next;
    });
  };

  const handleAdminLogin=()=> {
    setAdminAuthenticated(true);
    setCurrentStage('admin');
  };

  const handleAdminLogout=()=> {
    setAdminAuthenticated(false);
    setCurrentStage('questionnaire');
  };

  const generatePrompt=()=> {
    const customQuestions=(answers.customStrategyItems || [])
      .filter((item)=> item.q && item.a)
      .map((item)=> `**${item.q}:** ${item.a}`)
      .join('\n');

    return `### ROLE: Senior Website Strategist & Conversion Copywriter
### MISSION: Create a comprehensive website blueprint based on the following business intelligence.

### 1. BUSINESS INTELLIGENCE
**Business:** ${answers.businessName || 'N/A'}
**Email:** ${answers.businessEmail || 'N/A'}
**Industry:** ${answers.industry || 'N/A'}
**Tagline:** ${answers.tagline || 'N/A'}
**Core Description:** ${answers.description || 'N/A'}
**Problem Solved:** ${answers.problemSolved || 'N/A'}
**USP:** ${answers.differentiation || 'N/A'}

### 2. AUDIENCE PSYCHOLOGY
**Target Profile:** ${answers.primaryAudience || 'N/A'}
**Primary Pain Point:** ${answers.painPoints || 'N/A'}
**Dream Outcome:** ${answers.audienceGoals || 'N/A'}
**Common Objections:** ${answers.objections || 'N/A'}

### 3. STRATEGIC GOALS
**Primary Objective:** ${answers.primaryGoal || 'N/A'}
**Primary CTA:** ${answers.desiredAction || 'N/A'}

### 4. BRAND & DESIGN
**Personality:** ${answers.brandPersonality || 'N/A'}
**Tone:** ${answers.toneOfVoice || 'N/A'}
**Style:** ${answers.visualStyle || 'N/A'}

### 5. SCOPE
**Pages:** ${answers.requiredPages?.join(',') || 'N/A'}
**Priority:** ${answers.priorityPages || 'N/A'}

${customQuestions ? `### 6. CUSTOM CONTEXT\n${customQuestions}` : ''}`;
  };

  if (currentStage==='admin-login') {
    return (
      <AdminLogin
        onLogin={handleAdminLogin}
        onBack={()=> setCurrentStage('questionnaire')}
      />
    );
  }

  if (currentStage==='admin' && adminAuthenticated) {
    return (
      <AdminDashboard
        onLogout={handleAdminLogout}
        onBack={()=> setCurrentStage('questionnaire')}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-12">
      <AnimatePresence mode="wait">
        {currentStage==='questionnaire' ? (
          <motion.div
            key="q"
            initial={{opacity: 0}}
            animate={{opacity: 1}}
            exit={{opacity: 0}}
            className="max-w-4xl mx-auto p-6"
          >
            <header className="mb-12 pt-8 flex items-start justify-between gap-6">
              <div>
                <h1 className="text-4xl font-extrabold text-gray-900 mb-3 tracking-tight">
                  Questionnaire
                </h1>
                <p className="text-lg text-gray-600">
                  Please provide your project details below.
                </p>
              </div>

              <button
                onClick={()=> setCurrentStage('admin-login')}
                className="mt-2 px-4 py-2 rounded-xl border border-gray-200 bg-white text-gray-600 hover:text-blue-600 hover:border-blue-200 flex items-center gap-2 text-sm font-semibold"
              >
                <SafeIcon icon={FiShield} /> Admin
              </button>
            </header>

            <div className="space-y-6">
              {initialSections.map((section,index)=> (
                <QuestionnaireSection
                  key={index}
                  index={index}
                  section={section}
                  isOpen={expandedSections.has(index)}
                  onToggle={()=> toggleSection(index)}
                  answers={answers}
                  onChange={handleAnswerChange}
                />
              ))}
            </div>

            <div className="mt-12 flex justify-center">
              <button
                onClick={()=> setCurrentStage('thank-you')}
                className="px-10 py-4 bg-blue-600 text-white rounded-2xl hover:bg-blue-700 flex items-center gap-3 shadow-xl hover:shadow-2xl transition-all active:scale-95 font-bold text-lg"
              >
                Submit <SafeIcon icon={FiSend} />
              </button>
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="ty"
            initial={{opacity: 0}}
            animate={{opacity: 1}}
            exit={{opacity: 0}}
          >
            <ThankYouPage
              prompt={generatePrompt()}
              onBack={()=> setCurrentStage('questionnaire')}
              answers={answers}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default App;